import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { ASSET_BASE_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Profile.css';

interface User {
  id: number;
  username: string;
  email: string;
  created_at: string;
  avatar_url?: string;
  banner_url?: string;
  banner_offset_y?: number;
  bio?: string;
  favorite_color?: string;
  status?: string;
  allow_private_messages?: number;
}

interface UserStats {
  total_ducks_found: number;
  total_ships: number;
  first_find_date?: string;
  last_find_date?: string;
}

interface FollowStats {
  follower_count: number;
  following_count: number;
  is_following: boolean;
}

interface InvitationStats {
  total_invitations: number;
  successful_invitations: number;
  badge: string;
}

interface FollowingUser {
  id: number;
  username: string;
  avatar_url?: string;
  bio?: string;
  created_at: string;
  status?: string;
}

interface DuckLog {
  id: number;
  ship_name: string;
  found_date: string;
  image_url?: string;
  like_count?: number;
}

const Profile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user: authUser, updateUser: updateAuthUser } = useAuth();
  const authUserId = authUser?.id;
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [logs, setLogs] = useState<DuckLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [bioInput, setBioInput] = useState('');
  const [bioSaving, setBioSaving] = useState(false);
  const [bioMessage, setBioMessage] = useState('');
  const [bioError, setBioError] = useState('');
  const [editingBio, setEditingBio] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [bannerOffset, setBannerOffset] = useState(50);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null);
  const [pendingBannerFile, setPendingBannerFile] = useState<File | null>(null);
  const [bannerEditorOpen, setBannerEditorOpen] = useState(false);
  const [tempBannerOffset, setTempBannerOffset] = useState(50);
  const [isDraggingBanner, setIsDraggingBanner] = useState(false);
  const bannerPreviewRef = useRef<HTMLDivElement | null>(null);
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    next: '',
    confirm: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [followStats, setFollowStats] = useState<FollowStats | null>(null);
  const [followLoading, setFollowLoading] = useState(false);
  const [listModal, setListModal] = useState<{
    type: 'followers' | 'following';
    items: FollowingUser[];
  } | null>(null);
  const [listModalLoading, setListModalLoading] = useState(false);
  const [listModalError, setListModalError] = useState('');
  const [modalActionId, setModalActionId] = useState<number | null>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [messageSettingsModalOpen, setMessageSettingsModalOpen] = useState(false);
  const optionsRef = useRef<HTMLDivElement | null>(null);
  const [favorites, setFavorites] = useState<DuckLog[]>([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesPage, setFavoritesPage] = useState(1);
  const [favoritesTotalPages, setFavoritesTotalPages] = useState(1);
  const [isBlocked, setIsBlocked] = useState(false);
  const [invitationStats, setInvitationStats] = useState<InvitationStats | null>(null);
  const [blockLoading, setBlockLoading] = useState(false);
  const [invitationModalOpen, setInvitationModalOpen] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [newInviteCode, setNewInviteCode] = useState<{ code: string; url: string } | null>(null);
  const [invitationError, setInvitationError] = useState('');
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<FollowingUser[]>([]);
  const [blockedUsersLoading, setBlockedUsersLoading] = useState(false);
  const [allowPrivateMessages, setAllowPrivateMessages] = useState(true);
  const [messageSettingsSaving, setMessageSettingsSaving] = useState(false);
  const [messageSettingsMessage, setMessageSettingsMessage] = useState('');
  const [messageSettingsError, setMessageSettingsError] = useState('');

  const isOwnProfile = authUserId === Number(userId);

  const fetchBlockStatus = useCallback(async () => {
    if (!authUser || !user || isOwnProfile) return;
    try {
      const response = await api.get(`/blocks/${user.id}/status`);
      setIsBlocked(response.data.isBlocked || false);
    } catch (error) {
      console.error('Fehler beim Laden des Block-Status:', error);
    }
  }, [authUser, user, isOwnProfile]);

  const fetchBlockedUsers = useCallback(async () => {
    if (!isOwnProfile) return;
    try {
      setBlockedUsersLoading(true);
      const response = await api.get('/blocks/blocked');
      setBlockedUsers(response.data.blockedUsers || []);
    } catch (error) {
      console.error('Fehler beim Laden der blockierten User:', error);
    } finally {
      setBlockedUsersLoading(false);
    }
  }, [isOwnProfile]);

  const fetchProfile = useCallback(async () => {
    try {
      const [userResponse, statsResponse, logsResponse] = await Promise.all([
        api.get(`/users/${userId}`),
        api.get(`/users/${userId}/stats`),
        api.get(`/ducks/logs/user/${userId}`),
      ]);

      setUser(userResponse.data.user);
      setBioInput(userResponse.data.user.bio || '');
      const initialOffset =
        typeof userResponse.data.user.banner_offset_y === 'number'
          ? userResponse.data.user.banner_offset_y
          : 50;
      setBannerOffset(initialOffset);
      setTempBannerOffset(initialOffset);
      setAllowPrivateMessages(userResponse.data.user.allow_private_messages !== 0);
      setStats(statsResponse.data.stats);
      setLogs(logsResponse.data.logs);

      if (authUser) {
        try {
          const followResponse = await api.get(`/follows/stats/${userId}`);
          setFollowStats(followResponse.data);
        } catch {
          setFollowStats(null);
        }
      } else {
        setFollowStats(null);
      }

      // Einladungsstatistik laden (für alle sichtbar)
      try {
        const invitationResponse = await api.get(`/invitations/stats/${userId}`);
        setInvitationStats(invitationResponse.data);
      } catch (error) {
        // Fehler ignorieren - Einladungsstatistik ist optional
        console.log('Einladungsstatistik konnte nicht geladen werden:', error);
        setInvitationStats(null);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Profils:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, authUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    return () => {
      if (bannerPreviewUrl) {
        URL.revokeObjectURL(bannerPreviewUrl);
      }
    };
  }, [bannerPreviewUrl]);

  useEffect(() => {
    const endDrag = () => setIsDraggingBanner(false);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchend', endDrag);
    return () => {
      window.removeEventListener('mouseup', endDrag);
      window.removeEventListener('touchend', endDrag);
    };
  }, []);

  useEffect(() => {
    if (!optionsOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
        setOptionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [optionsOpen]);

  useEffect(() => {
    if (!passwordModalOpen) {
      setPasswordForm({ current: '', next: '', confirm: '' });
      setPasswordMessage('');
      setPasswordError('');
    }
  }, [passwordModalOpen]);

  const handleBannerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setBannerError('Die Datei ist zu groß (maximal 8MB)');
      return;
    }
    if (bannerPreviewUrl) {
      URL.revokeObjectURL(bannerPreviewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setPendingBannerFile(file);
    setBannerPreviewUrl(previewUrl);
    setTempBannerOffset(bannerOffset);
    setBannerEditorOpen(true);
    setBannerError('');
  };

  const resetBannerPreviewState = () => {
    if (bannerPreviewUrl) {
      URL.revokeObjectURL(bannerPreviewUrl);
    }
    setBannerPreviewUrl(null);
    setPendingBannerFile(null);
    setBannerEditorOpen(false);
    setIsDraggingBanner(false);
    setTempBannerOffset(bannerOffset);
  };

  const handleBannerEditorCancel = () => {
    resetBannerPreviewState();
  };

  const handleBannerEditorSave = async () => {
    if (!pendingBannerFile) {
      return;
    }
    setBannerError('');
    setBannerUploading(true);
    try {
      const formData = new FormData();
      formData.append('banner', pendingBannerFile);
      formData.append('banner_offset_y', String(Math.round(tempBannerOffset)));
      const response = await api.put('/users/me/banner', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newBannerUrl = response.data.bannerUrl;
      const newOffset =
        typeof response.data.banner_offset_y === 'number'
          ? response.data.banner_offset_y
          : tempBannerOffset;
      setUser((prev) =>
        prev
          ? {
              ...prev,
              banner_url: newBannerUrl,
              banner_offset_y: newOffset,
            }
          : prev
      );
      setBannerOffset(newOffset);
      if (authUser?.id === Number(userId)) {
        updateAuthUser({ banner_url: newBannerUrl, banner_offset_y: newOffset });
      }
      resetBannerPreviewState();
    } catch (error: any) {
      setBannerError(error.response?.data?.error || 'Fehler beim Aktualisieren des Banners');
    } finally {
      setBannerUploading(false);
    }
  };

  const updateTempOffsetFromEvent = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!bannerPreviewRef.current) {
      return;
    }
    const clientY =
      'touches' in event && event.touches.length
        ? event.touches[0].clientY
        : (event as React.MouseEvent).clientY;
    const rect = bannerPreviewRef.current.getBoundingClientRect();
    const percentage = ((clientY - rect.top) / rect.height) * 100;
    const clamped = Math.min(100, Math.max(0, percentage));
    setTempBannerOffset(clamped);
  };

  const handleBannerDragStart = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    setIsDraggingBanner(true);
    updateTempOffsetFromEvent(event);
  };

  const handleBannerDragMove = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (!isDraggingBanner) {
      return;
    }
    event.preventDefault();
    updateTempOffsetFromEvent(event);
  };

  const handleBannerDragEnd = () => {
    setIsDraggingBanner(false);
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) {
      return;
    }

    const file = event.target.files[0];

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Die Datei ist zu groß (maximal 5MB)');
      return;
    }

    setUploadError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await api.put('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newAvatarUrl = response.data.avatarUrl;

      setUser((prev) => (prev ? { ...prev, avatar_url: newAvatarUrl } : prev));

      if (authUser?.id === Number(userId)) {
        updateAuthUser({ avatar_url: newAvatarUrl });
      }
    } catch (error: any) {
      setUploadError(error.response?.data?.error || 'Fehler beim Hochladen des Avatars');
    } finally {
      setUploading(false);
    }
  };

  const handleBioSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setBioMessage('');
    setBioError('');

    try {
      setBioSaving(true);
      const response = await api.put('/users/me/profile', { bio: bioInput });
      const newBio = response.data.bio;

      setUser((prev) => (prev ? { ...prev, bio: newBio } : prev));

      if (authUser?.id === Number(userId)) {
        updateAuthUser({ bio: newBio });
      }

      setBioMessage('Biographie aktualisiert');
      setEditingBio(false);
    } catch (error: any) {
      setBioError(error.response?.data?.error || 'Fehler beim Speichern der Biographie');
    } finally {
      setBioSaving(false);
    }
  };

  const handlePasswordChange = async (event: React.FormEvent) => {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (!passwordForm.current || !passwordForm.next) {
      setPasswordError('Bitte alle Felder ausfüllen.');
      return;
    }
    if (passwordForm.next.length < 8) {
      setPasswordError('Das neue Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }

    try {
      setPasswordSaving(true);
      await api.put('/users/me/password', {
        current_password: passwordForm.current,
        new_password: passwordForm.next,
      });
      setPasswordMessage('Passwort aktualisiert.');
      setPasswordForm({ current: '', next: '', confirm: '' });
      setTimeout(() => {
        setPasswordModalOpen(false);
      }, 1200);
    } catch (error: any) {
      setPasswordError(error.response?.data?.error || 'Passwort konnte nicht geändert werden.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const accentColor = user?.favorite_color || '#6b5bff';
  const profileStyle = useMemo(
    () => ({ '--profile-accent': accentColor } as React.CSSProperties),
    [accentColor]
  );
  const effectiveBannerOffset = bannerOffset;
  const bannerStyle = user?.banner_url
    ? {
        backgroundImage: `url(${ASSET_BASE_URL}${user.banner_url})`,
        backgroundPosition: `center ${effectiveBannerOffset}%`,
      }
    : { backgroundPosition: `center ${effectiveBannerOffset}%` };

  const handleToggleFollow = async () => {
    if (!authUser || !user) return;
    if (authUser.id === user.id) return;
    try {
      setFollowLoading(true);
      const isCurrentlyFollowing = followStats?.is_following ?? false;
      if (isCurrentlyFollowing) {
        await api.delete(`/follows/${user.id}`);
        setFollowStats((prev) => ({
          follower_count: Math.max(0, (prev?.follower_count ?? 0) - 1),
          following_count: prev?.following_count ?? 0,
          is_following: false,
        }));
      } else {
        await api.post(`/follows/${user.id}`);
        setFollowStats((prev) => ({
          follower_count: (prev?.follower_count ?? 0) + 1,
          following_count: prev?.following_count ?? 0,
          is_following: true,
        }));
      }
      // Stats neu laden, um sicherzustellen, dass alles synchron ist
      try {
        const followResponse = await api.get(`/follows/stats/${userId}`);
        setFollowStats(followResponse.data);
      } catch {
        // Ignore refresh error
      }
    } catch (err: any) {
      console.error('Follow/Unfollow fehlgeschlagen', err);
      alert(err?.response?.data?.error || 'Fehler beim Folgen/Entfolgen');
    } finally {
      setFollowLoading(false);
    }
  };

  const openFollowList = async (type: 'followers' | 'following') => {
    if (!user) {
      return;
    }
    setListModal({ type, items: [] });
    setListModalLoading(true);
    setListModalError('');
    try {
      const endpoint =
        type === 'followers'
          ? `/follows/followers/${user.id}`
          : `/follows/following/${user.id}`;
      const response = await api.get(endpoint);
      const items =
        type === 'followers' ? response.data.followers ?? [] : response.data.following ?? [];
      setListModal({ type, items });
    } catch (err: any) {
      setListModalError(err?.response?.data?.error || 'Liste konnte nicht geladen werden.');
    } finally {
      setListModalLoading(false);
    }
  };

  const closeFollowList = () => {
    setListModal(null);
    setListModalError('');
  };

  const fetchFavorites = useCallback(async () => {
    if (!isOwnProfile) return;
    try {
      setFavoritesLoading(true);
      const response = await api.get(`/likes/user/favorites?page=${favoritesPage}&limit=12`);
      setFavorites(response.data.favorites || []);
      setFavoritesTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Fehler beim Laden der Favoriten:', error);
    } finally {
      setFavoritesLoading(false);
    }
  }, [isOwnProfile, favoritesPage]);

  useEffect(() => {
    if (isOwnProfile) {
      fetchFavorites();
      fetchBlockedUsers();
    } else {
      fetchBlockStatus();
    }
  }, [isOwnProfile, fetchFavorites, fetchBlockedUsers, fetchBlockStatus]);

  const handleModalUnfollow = async (targetUserId: number) => {
    if (!listModal || listModal.type !== 'following' || authUserId !== Number(userId)) {
      return;
    }
    try {
      setModalActionId(targetUserId);
      await api.delete(`/follows/${targetUserId}`);
      setListModal((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.filter((entry) => entry.id !== targetUserId),
            }
          : prev
      );
      setFollowStats((prev) =>
        prev
          ? {
              ...prev,
              following_count: Math.max(0, prev.following_count - 1),
            }
          : prev
      );
    } catch (err: any) {
      setListModalError(err?.response?.data?.error || 'Entfolgen nicht möglich.');
    } finally {
      setModalActionId(null);
    }
  };

  const handleMessageSettingsChange = async (enabled: boolean) => {
    setMessageSettingsMessage('');
    setMessageSettingsError('');
    try {
      setMessageSettingsSaving(true);
      await api.put('/users/me/message-settings', { allow_private_messages: enabled });
      setAllowPrivateMessages(enabled);
      setUser((prev) => (prev ? { ...prev, allow_private_messages: enabled ? 1 : 0 } : null));
      setMessageSettingsMessage(enabled ? 'Private Nachrichten aktiviert' : 'Private Nachrichten deaktiviert');
      setTimeout(() => setMessageSettingsMessage(''), 3000);
    } catch (error: any) {
      setMessageSettingsError(error.response?.data?.error || 'Einstellung konnte nicht gespeichert werden');
    } finally {
      setMessageSettingsSaving(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!authUser || !user || isOwnProfile) return;
    try {
      setBlockLoading(true);
      if (isBlocked) {
        await api.delete(`/blocks/${user.id}`);
        setIsBlocked(false);
      } else {
        await api.post(`/blocks/${user.id}`);
        setIsBlocked(true);
      }
    } catch (err: any) {
      console.error('Block/Unblock fehlgeschlagen', err);
      alert(err?.response?.data?.error || 'Fehler beim Blockieren/Entblockieren');
    } finally {
      setBlockLoading(false);
    }
  };

  const handleUnblock = async (targetUserId: number) => {
    try {
      setModalActionId(targetUserId);
      await api.delete(`/blocks/${targetUserId}`);
      setBlockedUsers((prev) => prev.filter((u) => u.id !== targetUserId));
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Entblockieren nicht möglich.');
    } finally {
      setModalActionId(null);
    }
  };

  if (loading) {
    return <div className="loading">Lade Profil...</div>;
  }

  if (!user) {
    return <div className="error">Benutzer nicht gefunden</div>;
  }

  return (
    <div className="profile" style={profileStyle}>
      <div className="profile-banner" style={bannerStyle}>
        {isOwnProfile && (
          <label className="banner-upload">
            <input
              type="file"
              accept="image/*"
              onChange={handleBannerChange}
              disabled={bannerUploading}
            />
            <span>{bannerUploading ? 'Banner wird aktualisiert…' : 'Banner ändern'}</span>
          </label>
        )}
      </div>
      <div className="profile-header">
        <div className="profile-avatar-wrapper">
          {user.avatar_url ? (
            <img
              src={`${ASSET_BASE_URL}${user.avatar_url}`}
              alt={`${user.username} Avatar`}
              className="profile-avatar-image"
            />
          ) : (
            <div className="profile-avatar-placeholder">
              {user.username.charAt(0).toUpperCase()}
            </div>
          )}
          {isOwnProfile && (
            <label className="avatar-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                disabled={uploading}
              />
              <span>{uploading ? 'Wird aktualisiert...' : 'Avatar ändern'}</span>
            </label>
          )}
        </div>
        {uploadError && <p className="avatar-error">{uploadError}</p>}
        <h1>👤 {user.username}</h1>
        {invitationStats?.badge && invitationStats.badge.trim() !== '' && (
          <div className="profile-badge-container" title={`${invitationStats.successful_invitations} erfolgreiche Einladung${invitationStats.successful_invitations !== 1 ? 'en' : ''}`}>
            <span className="profile-badge">{invitationStats.badge}</span>
          </div>
        )}
        <p>Mitglied seit {new Date(user.created_at).toLocaleDateString('de-DE')}</p>
        {followStats && (
          <p className="follow-stats">
            <button
              type="button"
              className="follow-count-button"
              onClick={() => openFollowList('followers')}
            >
              <strong>{followStats.follower_count}</strong> Follower
            </button>
            <span> · </span>
            <button
              type="button"
              className="follow-count-button"
              onClick={() => openFollowList('following')}
            >
              <strong>{followStats.following_count}</strong> folgt
            </button>
          </p>
        )}
        {isOwnProfile && (
          <div className="profile-options-trigger" ref={optionsRef}>
            <button
              type="button"
              className="ghost-button"
              onClick={() => setOptionsOpen((prev) => !prev)}
            >
              Profiloptionen ▾
            </button>
            {optionsOpen && (
              <div className="profile-options-menu">
                <button
                  type="button"
                  onClick={() => {
                    setPasswordModalOpen(true);
                    setOptionsOpen(false);
                  }}
                >
                  Passwort ändern
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMessageSettingsModalOpen(true);
                    setOptionsOpen(false);
                  }}
                >
                  Nachrichteneinstellungen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setInvitationModalOpen(true);
                    setOptionsOpen(false);
                  }}
                >
                  🎁 Einladungscode erstellen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setQrCodeModalOpen(true);
                    setOptionsOpen(false);
                  }}
                >
                  📱 QR-Code für Entenkarte
                </button>
              </div>
            )}
          </div>
        )}
        {!isOwnProfile && authUser && (
          <div className="profile-actions-row">
            <button
              type="button"
              className="ghost-button"
              onClick={handleToggleFollow}
              disabled={followLoading || !authUser}
            >
              {followLoading
                ? '...'
                : followStats?.is_following
                ? 'Nicht mehr folgen'
                : 'Folgen'}
            </button>
            <button
              type="button"
              className={`block-button ${isBlocked ? 'blocked' : ''}`}
              onClick={handleToggleBlock}
              disabled={blockLoading || !authUser}
            >
              {blockLoading ? '...' : isBlocked ? '✓ Blockiert' : '🚫 Blockieren'}
            </button>
            <Link to={`/messages?user=${user.id}`} className="button-primary">
              Nachricht senden
            </Link>
          </div>
        )}

        <div className="profile-bio">
          <div className="profile-bio-header">
            <h2>Biographie</h2>
            {isOwnProfile && (
              editingBio ? (
                <span className="bio-count">{bioInput.length}/500</span>
              ) : (
                <button
                  className="bio-edit-button"
                  onClick={() => {
                    setEditingBio(true);
                    setBioMessage('');
                    setBioError('');
                    setBioInput(user.bio || '');
                  }}
                >
                  Biographie bearbeiten
                </button>
              )
            )}
          </div>
          {isOwnProfile && editingBio ? (
            <form className="profile-bio-form" onSubmit={handleBioSave}>
              <textarea
                value={bioInput}
                onChange={(e) => setBioInput(e.target.value.slice(0, 500))}
                placeholder="Erzähle der Community etwas über dich, deine Lieblingsschiffe oder deine Cruising-Duck-Abenteuer."
                rows={5}
              />
              {bioError && <p className="bio-error">{bioError}</p>}
              {bioMessage && <p className="bio-success">{bioMessage}</p>}
              <div className="bio-actions">
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => {
                    setEditingBio(false);
                    setBioError('');
                    setBioMessage('');
                    setBioInput(user.bio || '');
                  }}
                  disabled={bioSaving}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="button-primary"
                  disabled={bioSaving}
                >
                  {bioSaving ? 'Speichern...' : 'Bio speichern'}
                </button>
              </div>
            </form>
          ) : (
            <p className="profile-bio-text">
              {user.bio && user.bio.trim().length > 0
                ? user.bio
                : 'Keine Biographie vorhanden.'}
            </p>
          )}
        </div>
      </div>
      {bannerError && <p className="profile-banner-error">{bannerError}</p>}

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h2>{stats.total_ducks_found}</h2>
            <p>Gefundene Enten</p>
          </div>
          <div className="stat-card">
            <h2>{stats.total_ships}</h2>
            <p>Verschiedene Schiffe</p>
          </div>
          {invitationStats && invitationStats.successful_invitations > 0 && (
            <div className="stat-card">
              <h2>{invitationStats.successful_invitations}</h2>
              <p>Eingeladene Benutzer</p>
              {invitationStats.badge && (
                <div className="stat-badge-small">{invitationStats.badge}</div>
              )}
            </div>
          )}
          {stats.first_find_date && (
            <div className="stat-card">
              <h2>{new Date(stats.first_find_date).toLocaleDateString('de-DE')}</h2>
              <p>Erste Ente gefunden</p>
            </div>
          )}
          {stats.last_find_date && (
            <div className="stat-card">
              <h2>{new Date(stats.last_find_date).toLocaleDateString('de-DE')}</h2>
              <p>Letzte Ente gefunden</p>
            </div>
          )}
        </div>
      )}

      <div className="profile-logs">
        <h2>Geloggte Enten</h2>
        {logs.length === 0 ? (
          <p>Noch keine Enten geloggt.</p>
        ) : (
          <div className="logs-grid">
            {logs.map((log) => (
              <Link key={log.id} to={`/log/${log.id}`} className="log-card-small">
                {log.image_url && (
                  <img
                    src={`${ASSET_BASE_URL}${log.image_url}`}
                    alt="Ente"
                    className="log-image-small"
                  />
                )}
                <div className="log-info-small">
                  <h3>{log.ship_name}</h3>
                  <p>{new Date(log.found_date).toLocaleDateString('de-DE')}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {isOwnProfile && (
        <div className="profile-logs">
          <h2>❤️ Meine Favoriten</h2>
          {favoritesLoading ? (
            <p>Favoriten werden geladen...</p>
          ) : favorites.length === 0 ? (
            <p>Noch keine Favoriten. Like Logs, um sie hier zu sehen!</p>
          ) : (
            <>
              <div className="logs-grid">
                {favorites.map((log) => (
                  <Link key={log.id} to={`/log/${log.id}`} className="log-card-small">
                    {log.image_url && (
                      <img
                        src={`${ASSET_BASE_URL}${log.image_url}`}
                        alt="Ente"
                        className="log-image-small"
                      />
                    )}
                    <div className="log-info-small">
                      <h3>{log.ship_name}</h3>
                      <p>{new Date(log.found_date).toLocaleDateString('de-DE')}</p>
                      {log.like_count !== undefined && (
                        <p className="log-favorite-count">❤️ {log.like_count}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
              {favoritesTotalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setFavoritesPage(favoritesPage - 1)}
                    disabled={favoritesPage === 1}
                  >
                    Zurück
                  </button>
                  <span>Seite {favoritesPage} von {favoritesTotalPages}</span>
                  <button
                    onClick={() => setFavoritesPage(favoritesPage + 1)}
                    disabled={favoritesPage === favoritesTotalPages}
                  >
                    Weiter
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isOwnProfile && (
        <div className="profile-logs">
          <h2>🚫 Blockierte Benutzer</h2>
          {blockedUsersLoading ? (
            <p>Blockierte Benutzer werden geladen...</p>
          ) : blockedUsers.length === 0 ? (
            <p>Du hast noch keine Benutzer blockiert.</p>
          ) : (
            <div className="blocked-users-list">
              {blockedUsers.map((blockedUser) => (
                <div key={blockedUser.id} className="blocked-user-item">
                  <Link to={`/profile/${blockedUser.id}`} className="blocked-user-link">
                    {blockedUser.avatar_url ? (
                      <img
                        src={`${ASSET_BASE_URL}${blockedUser.avatar_url}`}
                        alt={`${blockedUser.username} Avatar`}
                        className="blocked-user-avatar"
                      />
                    ) : (
                      <div className="blocked-user-avatar-placeholder">
                        {blockedUser.username?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="blocked-user-name">{blockedUser.username || 'Unbekannter Benutzer'}</span>
                  </Link>
                  <button
                    className="unblock-button"
                    onClick={() => handleUnblock(blockedUser.id)}
                    disabled={modalActionId === blockedUser.id}
                  >
                    {modalActionId === blockedUser.id ? '...' : 'Entblockieren'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {listModal && (
        <div className="follow-modal-overlay" onClick={closeFollowList}>
          <div className="follow-modal" onClick={(event) => event.stopPropagation()}>
            <div className="follow-modal-header">
              <h3>{listModal.type === 'followers' ? 'Follower' : 'Folgt'}</h3>
              <button type="button" className="ghost-button" onClick={closeFollowList}>
                Schließen
              </button>
            </div>
            {listModalError && <p className="follow-modal-error">{listModalError}</p>}
            {listModalLoading ? (
              <p className="follow-modal-loading">Lade...</p>
            ) : listModal.items.length === 0 ? (
              <p className="follow-modal-empty">Keine Einträge vorhanden.</p>
            ) : (
              <div className="follow-modal-list">
                {listModal.items.map((entry) => {
                  const displayName = entry.username || `User #${entry.id}`;
                  const initial =
                    entry.username && entry.username.length > 0
                      ? entry.username.charAt(0).toUpperCase()
                      : 'U';
                  return (
                    <div key={entry.id} className="follow-modal-item">
                      {entry.avatar_url ? (
                        <img
                          src={`${ASSET_BASE_URL}${entry.avatar_url}`}
                          alt={displayName}
                        />
                      ) : (
                        <div className="follow-modal-placeholder">{initial}</div>
                      )}
                      <div className="follow-modal-info">
                        <Link to={`/profile/${entry.id}`} onClick={closeFollowList}>
                          <strong>{displayName}</strong>
                        </Link>
                        <p>Seit {new Date(entry.created_at).toLocaleDateString('de-DE')}</p>
                      </div>
                      {isOwnProfile && listModal.type === 'following' && (
                        <button
                          type="button"
                          className="ghost-button danger follow-modal-unfollow"
                          onClick={() => handleModalUnfollow(entry.id)}
                          disabled={modalActionId === entry.id}
                        >
                          {modalActionId === entry.id ? '...' : 'Nicht mehr folgen'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {bannerEditorOpen && bannerPreviewUrl && (
        <div className="banner-editor-overlay">
          <div className="banner-editor-modal">
            <h3>Banner ausrichten</h3>
            <p className="banner-editor-copy">
              Ziehe das Bild mit der Maus oder nutze den Regler, um die Position einzustellen.
            </p>
            <div
              className={`banner-editor-preview${isDraggingBanner ? ' dragging' : ''}`}
              style={{
                backgroundImage: `url(${bannerPreviewUrl})`,
                backgroundPosition: `center ${tempBannerOffset}%`,
              }}
              ref={bannerPreviewRef}
              onMouseDown={handleBannerDragStart}
              onMouseMove={handleBannerDragMove}
              onMouseUp={handleBannerDragEnd}
              onMouseLeave={handleBannerDragEnd}
              onTouchStart={handleBannerDragStart}
              onTouchMove={handleBannerDragMove}
              onTouchEnd={handleBannerDragEnd}
            />
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(tempBannerOffset)}
              onChange={(e) => setTempBannerOffset(Number(e.target.value))}
            />
            <div className="banner-editor-values">
              <span>{Math.round(tempBannerOffset)}%</span>
            </div>
            <div className="banner-editor-actions">
              <button type="button" className="ghost-button" onClick={handleBannerEditorCancel}>
                Abbrechen
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={handleBannerEditorSave}
                disabled={bannerUploading}
              >
                {bannerUploading ? 'Speichert…' : 'Banner übernehmen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordModalOpen && (
        <div className="profile-settings-overlay">
          <div className="profile-settings-modal">
            <h3>Passwort ändern</h3>
            <p className="profile-settings-hint">
              Vergiss nicht: mindestens 8 Zeichen, am besten mit Zahlen & Sonderzeichen.
            </p>
            <form className="password-form" onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label htmlFor="currentPassword">Aktuelles Passwort</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordForm.current}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, current: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">Neues Passwort</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordForm.next}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, next: e.target.value }))
                  }
                  required
                  minLength={8}
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Bestätigung</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordForm.confirm}
                  onChange={(e) =>
                    setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }))
                  }
                  required
                  minLength={8}
                />
              </div>
              {passwordError && <p className="profile-settings-error">{passwordError}</p>}
              {passwordMessage && <p className="profile-settings-success">{passwordMessage}</p>}
              <div className="settings-actions">
                <button type="button" className="ghost-button" onClick={() => setPasswordModalOpen(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="button-primary" disabled={passwordSaving}>
                  {passwordSaving ? 'Speichert…' : 'Speichern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {messageSettingsModalOpen && (
        <div className="profile-settings-overlay">
          <div className="profile-settings-modal">
            <h3>💬 Nachrichteneinstellungen</h3>
            <div className="message-settings-content">
              <label className="message-settings-toggle">
                <input
                  type="checkbox"
                  checked={allowPrivateMessages}
                  onChange={(e) => handleMessageSettingsChange(e.target.checked)}
                  disabled={messageSettingsSaving}
                />
                <span>Private Nachrichten empfangen</span>
              </label>
              {messageSettingsMessage && (
                <p className="message-settings-feedback success">{messageSettingsMessage}</p>
              )}
              {messageSettingsError && (
                <p className="message-settings-feedback error">{messageSettingsError}</p>
              )}
              <p className="message-settings-hint">
                Wenn deaktiviert, können dir nur System und Admins Nachrichten senden.
              </p>
            </div>
            <div className="settings-actions">
              <button
                type="button"
                className="ghost-button"
                onClick={() => {
                  setMessageSettingsModalOpen(false);
                  setMessageSettingsMessage('');
                  setMessageSettingsError('');
                }}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {invitationModalOpen && (
        <div className="profile-settings-overlay">
          <div className="profile-settings-modal">
            <h3>🎁 Einladungscode erstellen</h3>
            <p className="profile-settings-hint">
              Generiere einen Einladungscode, um Freunde einzuladen. Jeder Code kann einmal verwendet werden.
            </p>
            
            {invitationError && (
              <p className="message-settings-feedback error">{invitationError}</p>
            )}

            {newInviteCode ? (
              <div className="invitation-code-result">
                <div className="form-group">
                  <label>Einladungscode:</label>
                  <div className="code-display-wrapper">
                    <input
                      type="text"
                      value={newInviteCode.code}
                      readOnly
                      className="code-input-display"
                    />
                    <button
                      type="button"
                      className="copy-button-small"
                      onClick={() => {
                        navigator.clipboard.writeText(newInviteCode.code);
                      }}
                      title="Code kopieren"
                    >
                      📋
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Einladungslink:</label>
                  <div className="code-display-wrapper">
                    <input
                      type="text"
                      value={newInviteCode.url}
                      readOnly
                      className="code-input-display"
                    />
                    <button
                      type="button"
                      className="copy-button-small"
                      onClick={() => {
                        navigator.clipboard.writeText(newInviteCode.url);
                      }}
                      title="Link kopieren"
                    >
                      📋
                    </button>
                  </div>
                </div>
                <div className="settings-actions">
                  <button
                    type="button"
                    className="ghost-button"
                    onClick={() => {
                      setInvitationModalOpen(false);
                      setNewInviteCode(null);
                      setInvitationError('');
                    }}
                  >
                    Schließen
                  </button>
                </div>
              </div>
            ) : (
              <div className="settings-actions">
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => {
                    setInvitationModalOpen(false);
                    setInvitationError('');
                  }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="button-primary"
                  onClick={async () => {
                    try {
                      setGeneratingCode(true);
                      setInvitationError('');
                      const response = await api.post('/invitations/generate');
                      setNewInviteCode({
                        code: response.data.invite_code,
                        url: response.data.invite_url,
                      });
                      // Statistik aktualisieren
                      try {
                        const statsResponse = await api.get(`/invitations/stats/${userId}`);
                        setInvitationStats(statsResponse.data);
                      } catch {
                        // Ignorieren
                      }
                    } catch (error: any) {
                      setInvitationError(error.response?.data?.error || 'Fehler beim Generieren des Codes');
                    } finally {
                      setGeneratingCode(false);
                    }
                  }}
                  disabled={generatingCode}
                >
                  {generatingCode ? 'Wird generiert...' : 'Code generieren'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {qrCodeModalOpen && isOwnProfile && (
        <div className="profile-settings-overlay">
          <div className="profile-settings-modal">
            <h3>📱 QR-Code für Entenkarte</h3>
            <p className="profile-settings-hint">
              Drucke diesen QR-Code auf deine Entenkarte. Wenn jemand den Code scannt, wird er direkt zur Log-Seite weitergeleitet und du wirst benachrichtigt, sobald deine Ente geloggt wurde.
            </p>
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              <img
                src={`/api/users/${userId}/qr-code?t=${Date.now()}`}
                alt="QR-Code für Entenkarte"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '20px',
                  background: 'white',
                }}
              />
            </div>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <a
                href={`/api/users/${userId}/qr-code?t=${Date.now()}`}
                download="duck-qr-code.png"
                className="button-primary"
                style={{ display: 'inline-block', textDecoration: 'none' }}
              >
                📥 QR-Code herunterladen
              </a>
            </div>
            <div className="settings-actions">
              <button type="button" className="ghost-button" onClick={() => setQrCodeModalOpen(false)}>
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;

